import React, { useState, useEffect } from "react";
import { View, TextInput, StyleSheet, FlatList, Text, Alert, TouchableOpacity } from "react-native";
import { useQuery, useMutation } from "@apollo/client";
import { Ionicons } from '@expo/vector-icons';
import CampaignDropdown from "./CampaignDropdown";
import {
  CREATE_LANDING_PAGE_DROPDOWN_OPTION,
  UPDATE_LANDING_PAGE_DROPDOWN_OPTION,
  DELETE_LANDING_PAGE_DROPDOWN_OPTION,
} from "../graphql/campaign";
import { GET_LANDING_PAGE_DROPDOWN_OPTIONS } from "../graphql/queries";

type Mapping = {
  id: string;
  label: string;
  campaigns: { id: string; name: string }[];
};

export default function CampaignLabelMapper() {
  const [rows, setRows] = useState<Mapping[]>([]);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  const [isOpen, setIsOpen] = useState<string>("");


  const { data, loading, error, refetch } = useQuery(GET_LANDING_PAGE_DROPDOWN_OPTIONS, {
    fetchPolicy: "no-cache",
  });

  const [createOption] = useMutation(CREATE_LANDING_PAGE_DROPDOWN_OPTION);
  const [updateOption] = useMutation(UPDATE_LANDING_PAGE_DROPDOWN_OPTION);
  const [deleteOption] = useMutation(DELETE_LANDING_PAGE_DROPDOWN_OPTION);

  const toggleExpand = (id: string, isOpen: boolean) => {

    console.log('expanding', id)
  setExpandedRows((prev) => {
    const next = new Set(prev);
    if (isOpen) next.add(id);
    else next.delete(id);
    return next;
  });
};




  // Initialize rows without sorting
  useEffect(() => {
    if (data?.landingPageDropdownOptions?.landingPageDropdownOptions) {
      setRows([...data.landingPageDropdownOptions.landingPageDropdownOptions]);
    }
  }, [data]);

  // Update label
  const updateLabel = async (id: string, label: string) => {
    setRows(rows.map(r => r.id === id ? { ...r, label } : r));
    try {
      await updateOption({
        variables: { landingPageDropdownOptionId: id, landingPageDropdownOption: { label } },
      });
    } catch (err) {
      console.error(err);
      Alert.alert("Error", "Failed to update label");
    }
  };

  // Add campaign
  const addCampaign = async (optionId: string, campaignId?: string) => {
    if (!campaignId) return;
    try {
      await updateOption({
        variables: {
          landingPageDropdownOptionId: optionId,
          landingPageDropdownOption: { campaignsToAdd: [campaignId] },
        },
      });
      refetch();
    } catch (err) {
      console.error(err);
      Alert.alert("Error", "Failed to add campaign");
    }
  };

  // Remove campaign
  const removeCampaign = async (optionId: string, campaignId: string) => {
    try {
      await updateOption({
        variables: {
          landingPageDropdownOptionId: optionId,
          landingPageDropdownOption: { campaignsToRemove: [campaignId] },
        },
      });
      refetch();
    } catch (err) {
      console.error(err);
      Alert.alert("Error", "Failed to remove campaign");
    }
  };

  // Create a new surgery label (appended at the bottom)
  const createLabel = async () => {
    try {
      const { data } = await createOption({ variables: { label: "zNew Surgery" } });
      if (data?.createLandingPageDropdownOption?.landingPageDropdownOption) {
        setRows(prevRows => [...prevRows, data.createLandingPageDropdownOption.landingPageDropdownOption]);
      }
    } catch (err) {
      console.error(err);
      Alert.alert("Error", "Failed to create surgery");
    }
  };

  // Delete label
  const deleteLabel = async (id: string) => {
    if (deletingId) return;
    setDeletingId(id);

    try {
      await deleteOption({ variables: { landingPageDropdownOptionIds: [id] } });
      setRows(prevRows => prevRows.filter(r => r.id !== id));
    } catch (err) {
      console.error(err);
      Alert.alert("Error", "Failed to delete surgery");
    } finally {
      setDeletingId(null);
    }
  };

  if (loading) return <Text>Loading...</Text>;
  if (error) return <Text>Error loading options</Text>;



  const testing = () => {

    console.log('expandedRows',expandedRows)

  }

 return (
    <View style={{ padding: 10 }}>
      <FlatList
        data={rows}
        keyExtractor={(item) => item.id}
        renderItem={({ item }) => (
          <View
            style={[
              styles.row,
              expandedRows.has(item.id) && { marginBottom: 200, zIndex: 1000, elevation: 10 },
            ]}
          >
            {/* Label input and delete */}
            <View style={styles.labelRow}>
              <TextInput
                style={styles.input}
                value={item.label}
                onChangeText={(text) => updateLabel(item.id, text)}
              />
              <TouchableOpacity onPress={() => deleteLabel(item.id)} style={styles.deleteIcon}>
                <Ionicons name="trash-outline" size={20} color="red" />
              </TouchableOpacity>
            </View>

            {/* Campaign buttons */}
            <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 5, marginBottom: 10 }}>
              {item.campaigns.map((c) => (
                <View key={c.id} style={styles.campaignButtonWrapper}>
                  <Text style={styles.campaignButtonText}>{c.name}</Text>
                  <TouchableOpacity onPress={() => removeCampaign(item.id, c.id)} style={styles.campaignRemove}>
                    <Ionicons name="close-circle" size={16} color="red" />
                  </TouchableOpacity>
                </View>
              ))}
            </View>

            {/* Campaign dropdown */}
            <CampaignDropdown
              onclick={testing}
              instantOnly={false}
              value={item.campaigns.length > 0 ? item.campaigns[0].id : undefined}
              setValue={(id) => addCampaign(item.id, id)}
              opener={true}
            />
          </View>
        )}
      />

      {/* Add surgery button */}
      <TouchableOpacity onPress={createLabel} style={styles.addButton}>
        <Text style={styles.addButtonText}>+ Add Surgery</Text>
      </TouchableOpacity>
    </View>
  );
}

const styles = StyleSheet.create({
  row: {
    marginBottom: 16,
    padding: 12,
    borderWidth: 1,
    borderColor: "#ddd",
    borderRadius: 8,
    backgroundColor: "#fff",
    zIndex: 1,
  },
  labelRow: {
    flexDirection: "row",
    alignItems: "center",
  },
  input: {
    flex: 1,
    borderWidth: 1,
    borderColor: "#ccc",
    padding: 10,
    borderRadius: 6,
    marginBottom: 10,
  },
  deleteIcon: {
    marginLeft: 8,
  },
  campaignButtonWrapper: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 8,
    paddingVertical: 4,
    backgroundColor: "#eee",
    borderRadius: 4,
    marginRight: 5,
    marginBottom: 5,
  },
  campaignRemove: {
    marginLeft: 4,
  },
  campaignButtonText: {
    fontSize: 12,
    color: "#333",
  },
  addButton: {
    marginTop: 10,
    padding: 12,
    backgroundColor: "#4DBA8E",
    borderRadius: 6,
    alignItems: "center",
    position: "relative",
    zIndex: 0,
  },
  addButtonText: {
    color: "#fff",
    fontWeight: "bold",
  },
 dropdownWrapper: {
    width: "100%",
  },
  expandedSpacer: {
    marginBottom: 200, // Or dynamic if you want
  },
});

